import React, { useState } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import MetalRateStrip from '../components/MetalRateStrip';
import OnboardingModal from '../components/OnboardingModal';
import './SellerPage.css';

const SellerPage = () => {
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authModalRole, setAuthModalRole] = useState('seller');

  const openAuth = (role) => {
    setAuthModalRole(role);
    setAuthModalOpen(true);
  };

  const sellerWorkflow = [
    {
      num: '01',
      title: 'Register',
      desc: 'Submit your jewellery business profile, GSTIN, PAN, and trade licenses online in under 5 minutes.',
    },
    {
      num: '02',
      title: 'Verify',
      desc: 'JMI compliance operators review credentials, hallmarking licenses, and verify your registered manufacturing facility.',
    },
    {
      num: '03',
      title: 'Add Products',
      desc: 'Upload product designs, define gross and net weight variants, karatage tiers, and standard making charge formulas.',
    },
    {
      num: '04',
      title: 'Receive Orders',
      desc: 'Get notified of structured purchase orders with verified buyer credentials and locked bullion rates.',
    },
    {
      num: '05',
      title: 'Prepare',
      desc: 'Fabricate or assemble hallmarked pieces, laser-inscribe HUID, and seal in barcoded tamper-evident custody pouches.',
    },
    {
      num: '06',
      title: 'Dispatch',
      desc: 'Hand over consignments to JMI-coordinated insured logistics carriers with digital custody handover receipts.',
    },
    {
      num: '07',
      title: 'Get Paid',
      desc: 'Receive direct bank RTGS settlement into your registered commercial account upon verified delivery.',
    },
  ];

  const sellerFeatures = [
    {
      icon: '💎',
      title: 'Digital Catalogue',
      desc: 'Create and maintain your cloud-based wholesale catalogue synchronized automatically with live Indian bullion spot prices.',
    },
    {
      icon: '📥',
      title: 'B2B Orders',
      desc: 'Receive structured wholesale purchase orders directly from verified retail jewellers without middlemen taking cuts.',
    },
    {
      icon: '⚖️',
      title: 'Product Variants',
      desc: 'Manage ring sizes, chain lengths, gross weights, net precious metal tolerances, and stone weights systematically.',
    },
    {
      icon: '⏱️',
      title: 'Order Preparation Workflow',
      desc: 'Update order progress in real time as jewellery moves through casting, filing, setting, polishing, and hallmarking.',
    },
    {
      icon: '🚚',
      title: 'Dispatch Management',
      desc: 'Coordinate vault pickups, carrier handovers, and buyer receipt confirmations.',
    },
    {
      icon: '🌐',
      title: 'Business Visibility',
      desc: 'Make your unique jewellery designs discoverable to thousands of retail showrooms and institutional buyers nationwide.',
    },
    {
      icon: '📊',
      title: 'Payment Tracking',
      desc: 'Track payments, advance deposits, escrow balances, and settlement schedules against individual purchase order lines.',
    },
  ];

  return (
    <div className="app-wrapper seller-page-view">
      <MetalRateStrip />
      <Navbar onOpenAuthModal={openAuth} />

      <main>
        {/* Seller Hero */}
        <section className="seller-hero-section">
          <div className="jmi-container seller-hero-grid">
            <div className="seller-hero-copy">
              <div className="section-label">SELLER & MANUFACTURER CHANNEL</div>
              <h1 className="seller-hero-title">
                Turn Your Jewellery Inventory into a <span className="gold-gradient-text">B2B Business Channel</span>
              </h1>
              <p className="seller-hero-sub">
                Reach jewellery buyers, showcase your products and manage wholesale orders through one connected platform.
              </p>

              <div className="seller-cta-group">
                <button 
                  className="btn btn-primary btn-lg"
                  onClick={() => openAuth('seller')}
                >
                  Become a JMI Seller →
                </button>
                <a 
                  href="https://play.google.com/store/apps/details?id=com.jmi.jmiseller" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="btn btn-secondary btn-lg"
                >
                  Download Seller App
                </a>
              </div>

              <div className="seller-quick-stats">
                <div className="stat-pill">
                  <strong>Pan-India</strong> Retail Reach
                </div>
                <div className="stat-pill">
                  <strong>Real-Time</strong> Inventory Locks
                </div>
                <div className="stat-pill">
                  <strong>Fast RTGS</strong> Settlements
                </div>
              </div>
            </div>

            {/* Manufacturer Wholesale Channel Overview Card */}
            <div className="seller-hero-dash-col">
              <div className="seller-channel-summary-card">
                <div className="ssc-header">
                  <div className="ssc-badge">
                    <span className="ssc-badge-dot"></span>
                    <span>MANUFACTURER DISTRIBUTION CHANNEL</span>
                  </div>
                  <span className="ssc-status-tag">Live Wholesale B2B</span>
                </div>

                <div className="ssc-hero-kpi">
                  <span className="ssc-kpi-lbl">National Showroom Reach</span>
                  <div className="ssc-kpi-val gold-gradient-text">5,000+ Retail Showrooms</div>
                  <span className="ssc-kpi-sub">Direct access to verified buyers with zero middleman brokerage</span>
                </div>

                <div className="ssc-specs-grid">
                  <div className="ssc-spec-item">
                    <span className="ssc-spec-lbl">Rate Protection</span>
                    <span className="ssc-spec-val gold">Real-Time Spot Lock</span>
                  </div>
                  <div className="ssc-spec-item">
                    <span className="ssc-spec-lbl">Settlement Term</span>
                    <span className="ssc-spec-val">Direct Bank RTGS</span>
                  </div>
                  <div className="ssc-spec-item">
                    <span className="ssc-spec-lbl">Logistics Link</span>
                    <span className="ssc-spec-val cyan">Insured Armored Transit</span>
                  </div>
                  <div className="ssc-spec-item">
                    <span className="ssc-spec-lbl">Assay Traceability</span>
                    <span className="ssc-spec-val">BIS HUID Compliant</span>
                  </div>
                </div>

                <div className="ssc-features-list">
                  <div className="ssc-feat-row">✓ Synchronized Multi-Variant Digital Catalogue (Gross & Net Weights)</div>
                  <div className="ssc-feat-row">✓ Structured Wholesale Orders with Locked Bullion Rates</div>
                  <div className="ssc-feat-row">✓ Automated Making Charge Formulas & Stone Deduction Audits</div>
                  <div className="ssc-feat-row">✓ Staged Dispatch Preparation with Barcoded Tamper Bags</div>
                  <div className="ssc-feat-row">✓ Accelerated Commercial Escrow Settlements on Counter Delivery</div>
                </div>

                <div className="ssc-footer-bar">
                  <button 
                    className="btn btn-primary w-full"
                    onClick={() => openAuth('seller')}
                  >
                    Register as Manufacturer / Seller →
                  </button>
                  <div className="ssc-corporate-credit">
                    Operated under <strong>7RAYS GLOBAL PRIVATE LIMITED</strong><br />
                    <span>CIN: U47733GJ2026PTC171774 • Surat, Gujarat, 395010 - India.</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 7-Step Seller Workflow */}
        <section className="seller-workflow-section">
          <div className="jmi-container">
            <div className="section-header-center">
              <div className="section-label">ONBOARDING TO SETTLEMENT</div>
              <h2 className="section-title">
                The Seller <span className="gold-gradient-text">Execution Workflow</span>
              </h2>
              <p className="section-subtitle">
                A seamless 7-step sequence empowering manufacturers and karigars to scale wholesale distribution effortlessly.
              </p>
            </div>

            <div className="seller-steps-grid">
              {sellerWorkflow.map((step, idx) => (
                <div key={idx} className="seller-workflow-card">
                  <div className="step-num-pill">{step.num}</div>
                  <h3 className="step-title">{step.title}</h3>
                  <p className="step-desc">{step.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Seller Features Suite */}
        <section className="seller-features-section">
          <div className="jmi-container">
            <div className="section-header-center">
              <div className="section-label">SELLER TOOLS & INFRASTRUCTURE</div>
              <h2 className="section-title">
                Everything You Need to <span className="gold-gradient-text">Scale Wholesale Volume</span>
              </h2>
              <p className="section-subtitle">
                Purpose-built digital infrastructure built exclusively for gold, silver, diamond, and platinum manufacturers.
              </p>
            </div>

            <div className="seller-features-grid">
              {sellerFeatures.map((feat, idx) => (
                <div key={idx} className="seller-feat-item">
                  <div className="feat-icon-box">{feat.icon}</div>
                  <h3 className="feat-title">{feat.title}</h3>
                  <p className="feat-desc">{feat.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Seller CTA Banner */}
        <section className="seller-cta-banner-section">
          <div className="jmi-container">
            <div className="seller-banner-card">
              <h2>Join India's Top Hallmarked Jewellery Manufacturers</h2>
              <p>
                Connect directly with thousands of verified retail jewellers seeking reliable wholesale suppliers.
              </p>
              <div className="banner-actions">
                <button 
                  className="btn btn-primary btn-lg"
                  onClick={() => openAuth('seller')}
                >
                  Register as Manufacturer / Seller →
                </button>
                <a 
                  href="https://play.google.com/store" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="btn btn-outline-gold btn-lg"
                >
                  Get JMI Seller App
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer onOpenAuthModal={openAuth} />
      <OnboardingModal 
        isOpen={authModalOpen} 
        onClose={() => setAuthModalOpen(false)} 
        initialRole={authModalRole} 
      />
    </div>
  );
};

export default SellerPage;
